import { defineRouteMetadata } from "@pax/contract-kit";
import { z } from "zod";
import type { Permission } from "./permissions";
import type { MinimumTier, RouteCost } from "./risk";

export {
  type ContractService,
  type ContractStage,
  errors,
  pathFor,
} from "@pax/contract-kit";

export type ServiceCaller = "game-server" | "agent-runner" | "preset-server" | "asset-editor";

/** A scope a Pax service access token can carry for a Product API route. */
export type ServiceScope =
  | "agent-state:read"
  | "agent-state:write"
  | "assets:search"
  | "basemaps:admit"
  | "catalog:read"
  | "drafts:storage"
  | "games:storage"
  | "moderation:judge"
  | "ai:call";

/** Each service that may call a route, and the scope its token must carry. */
export type ServiceCallers = Partial<Record<ServiceCaller, ServiceScope>>;

/** Who may call a route. Each kind is enforced by the route builder of the same name. */
export type RouteAuth = (
  | {
      kind: "session"; // any Better Auth session, guests included
      /**
       * The scope that lets the game server call the route for one game, as that game's owner. It
       * names the game in the gateway headers and sends a service access token carrying the scope.
       */
      actingForGame?: ServiceScope;
    }
  | { kind: "account"; permission?: Permission } // a session whose user is not a guest
  | { kind: "service"; callers: ServiceCallers } // another Pax service's access token
  | { kind: "catalog-publisher" } // a session whose user has the service role, such as the seed
) & {
  /** A route-specific sentence added after the generated access description. */
  note?: string;
};

/** A signed-in account holding `permission`. */
export function staffAuth(permission: Permission): RouteAuth {
  return { kind: "account", permission };
}

export interface PaxRouteMetadata {
  auth: RouteAuth;
  /**
   * The lowest standing tier the route accepts, or null for any. Service callers and the game
   * server skip the check.
   */
  minimumTier: MinimumTier | null;
  /** Which rate-limit budget the route draws from. Service callers aren't counted. */
  cost: RouteCost;
  /**
   * True on the few routes an account still on the waitlist may call, such as redeeming an invite
   * or deleting the account. Every other route refuses it with `ACCOUNT_WAITLISTED`.
   */
  openToWaitlist?: true;
}

export const { paxRoute, getPaxRouteMetadata } = defineRouteMetadata<PaxRouteMetadata>();

export const StreamResponse = z.object({
  status: z.literal(200),
  headers: z.record(z.string(), z.string()).optional(),
  body: z.instanceof(ReadableStream),
});
export type StreamResponse = z.infer<typeof StreamResponse>;
